// Migrated from /pages/home-page.tsx for Phase 5 feature organization
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, FileText, Wrench, TrendingUp, Calendar, Mail, ExternalLink } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { PageLayout } from '@/components/ui/page-layout';

interface DashboardStats {
  totalCustomers: number;
  totalProposals: number;
  totalInstallations: number;
  pipelineValue: number;
  recentActivities: Array<{
    id: number;
    type: string;
    description: string;
    timestamp: string;
  }>;
}

function StatCard({
  title,
  value,
  icon: Icon,
  description,
  isLoading,
  isCurrency = false,
}: {
  title: string;
  value: number;
  icon: any;
  description: string;
  isLoading: boolean;
  isCurrency?: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Icon className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-16 mb-2" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-[#054597]" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {isCurrency
            ? new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value)
            : value.toLocaleString()}
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

export default function HomePage() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['/api/dashboard/stats'],
  });

  return (
    <PageLayout title="Dashboard" description="Welcome to your solar management system">
      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Customers"
          value={stats?.totalCustomers || 0}
          icon={Users}
          description="Active customers in system"
          isLoading={isLoading}
        />
        <StatCard
          title="Proposals"
          value={stats?.totalProposals || 0}
          icon={FileText}
          description="Generated proposals"
          isLoading={isLoading}
        />
        <StatCard
          title="Installations"
          value={stats?.totalInstallations || 0}
          icon={Wrench}
          description="Completed installations"
          isLoading={isLoading}
        />
        <StatCard
          title="Pipeline Value"
          value={stats?.pipelineValue || 0}
          icon={TrendingUp}
          description="Total pipeline value"
          isLoading={isLoading}
          isCurrency={true}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Management
            </CardTitle>
            <CardDescription>Manage your customer pipeline and relationships</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild className="w-full">
              <Link href="/customers">View All Customers</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link href="/pipeline">Pipeline View</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Sales & Proposals
            </CardTitle>
            <CardDescription>Create and manage solar proposals</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild className="w-full">
              <Link href="/proposals">View Proposals</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link href="/opensolar">OpenSolar Designer</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Operations
            </CardTitle>
            <CardDescription>Track installations and project progress</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild className="w-full">
              <Link href="/installations">View Installations</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link href="/loa">Letters of Authority</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest updates across your solar projects</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-2 w-2 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : stats?.recentActivities?.length ? (
            <div className="space-y-4">
              {stats.recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-4">
                  <div className="w-2 h-2 bg-primary rounded-full" />
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium leading-none">{activity.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="secondary">{activity.type}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No recent activity to display</p>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
